/*
 * Same rules as theResume's libs/utils attribution.ts. Phase 3 will need a server copy of
 * sanitizeFirstTouch when attribution reaches the CRM lead record; keep the two in step then.
 *
 * First-touch marketing attribution: how a visitor first reached us. Captured on the first page
 * they land on (marketing site or app), carried from the marketing domain to the app domain on
 * CTA links, and registered as analytics super/person properties. It is not stored in our
 * database. It must never carry personal data, so everything goes through
 * `sanitizeFirstTouch` on both the client and the server.
 */

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];

export type FirstTouch = Partial<Record<UtmKey, string>> & {
  /** Referring page as origin + path, no query string or fragment. */
  referrer?: string;
  /** First page seen, as a path, no query string or fragment. */
  landing_page?: string;
  /** Host the first page was served on, e.g. "firstround.com.au". */
  landing_host?: string;
  /** ISO timestamp of the first visit. */
  captured_at: string;
};

const MAX_LENGTH = 200;
const EMAIL_LIKE = /[^\s@]+@[^\s@]+\.[^\s@]+/;

const cleanText = (value: unknown) => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim().slice(0, MAX_LENGTH);
  // An email pasted into a UTM (it happens with hand-built links) is personal data: drop it.
  if (!trimmed || EMAIL_LIKE.test(trimmed)) return undefined;
  return trimmed;
};

/** "https://www.linkedin.com/feed/?trk=x#y" -> "https://www.linkedin.com/feed/" */
const cleanUrl = (value: unknown) => {
  if (typeof value !== "string" || !value) return undefined;
  try {
    const url = new URL(value);
    if (url.protocol !== "http:" && url.protocol !== "https:") return undefined;
    return cleanText(`${url.origin}${url.pathname}`);
  } catch {
    return undefined;
  }
};

/** "/for-legal?x=1#faq" -> "/for-legal" */
const cleanPath = (value: unknown) => {
  if (typeof value !== "string" || !value.startsWith("/")) return undefined;
  return cleanText(value.split(/[?#]/)[0]);
};

const cleanHost = (value: unknown) => {
  const text = cleanText(value)?.toLowerCase();
  return text && /^[a-z0-9.-]+(:\d+)?$/.test(text) ? text : undefined;
};

/**
 * Keep only the known, PII-free fields of a first-touch record. Returns null when the input is
 * not a usable record (no valid timestamp).
 */
export const sanitizeFirstTouch = (input: unknown): FirstTouch | null => {
  if (!input || typeof input !== "object") return null;
  const raw = input as Record<string, unknown>;

  const capturedAt = typeof raw["captured_at"] === "string" ? new Date(raw["captured_at"]) : null;
  if (!capturedAt || Number.isNaN(capturedAt.getTime())) return null;

  const result: FirstTouch = { captured_at: capturedAt.toISOString() };
  for (const key of UTM_KEYS) {
    const value = cleanText(raw[key]);
    if (value) result[key] = value;
  }
  const referrer = cleanUrl(raw["referrer"]);
  if (referrer) result.referrer = referrer;
  const landingPage = cleanPath(raw["landing_page"]);
  if (landingPage) result.landing_page = landingPage;
  const landingHost = cleanHost(raw["landing_host"]);
  if (landingHost) result.landing_host = landingHost;
  return result;
};

/** Flatten for analytics super/person properties: { first_touch_utm_source: "linkedin", ... }. */
export const firstTouchProperties = (touch: FirstTouch) =>
  Object.fromEntries(
    Object.entries(touch).map(([key, value]) => [`first_touch_${key}`, value])
  ) as Record<string, string>;
